import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, FormControl, FormHelperText, FormLabel, Grid, MenuItem, Paper, Select, TableCell, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";

import Heading from "../../../component/Heading/Heading";
import CommonModal from "../../../component/model/ModelComponent";
import CommonButton from "../../../component/button/Button";
import TableComponent from "../../../component/Table/TableComponent";
import addCircleIcon from "../../../assets/icons/add-circle.svg";
import editIcon from "../../../assets/icons/Edit.svg";
import deleteIcon from "../../../assets/icons/trash.svg";
import Toast from "../../../component/toastify/Index";
import InputComponent from "../../../component/input/Input";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Download from '../../../assets/icons/download.svg';
import "../../../styles/pages/customer/verbindlichkeiten.scss";
import { createVerbindlichkeiten, deleteVerbindlichkeiten, getByIdVerbindlichkeiten, getVerbindlichkeitenLists, updateVerbindlichkeiten } from "../../../services/customer-services/customerVerbindlichkeiten";
import { formatAmount, formatGermanAmount, formatPlainAmount, formatUpdateNumber } from "../../../helper/formatAmount";
import { useCommonContext } from "../../../context/common";
import SelectCustomerPage from "../select-customer/SelectCustomerPage";
import GeneratePDF from "../../../component/PDFGenerator";
import { changeDateFormate } from "../../../helper/changeDateFormate";

const columns = [
    { field: "lieferant", headerName: "Lieferant", translationKey: 'verbindlichkeiten_lieferant' },
    { field: "rechnungsNummer", headerName: "Rechnungsnummer", translationKey: 'forderungen_rechnungsNummer' },
    { field: "rechnungsDatum", headerName: "Rechnungsdatum", translationKey: 'forderungen_rechnungsDatum' },
    { field: "fälligkeitsdatum", headerName: "Fälligkeitsdatum", translationKey: 'forderungen_fälligkeitsdatum' },
    { field: "nettoBetrag", headerName: "Netto Betrag", translationKey: 'forderungen_nettoBetrag' },
    { field: "umsatzsteuer", headerName: "Umsatzsteuer", translationKey: 'forderungen_umsatzsteuer' },
    { field: "steuer", headerName: "Steuer", translationKey: 'forderungen_steuer' },
    { field: "bruttoBetrag", headerName: "Brutto Betrag", translationKey: 'forderungen_bruttoBetrag' },
    { field: "invoiceAvailable", headerName: "Invoice Available", translationKey: 'common_invoice_available' },
    { field: "nettoOffen", headerName: "Netto Offen", translationKey: 'forderungen_nettoOffen' },
    { field: "zahlungsstatus", headerName: "Zahlungsstatus", translationKey: 'forderungen_zahlungsstatus' },
    { field: "action", headerName: "Aktion", translationKey: 'client_list_action' },
];

const Verbindlichkeiten = () => {
    const { t } = useTranslation();
    const [verbindlichkeiten, setVerbindlichkeiten] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);
    const [expanded, setExpanded] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [filterData, setFilterData] = useState({
        lieferant: '',
        rechnungsNummer: '',
        zahlungsstatus: '',
        invoiceAvailable: '',
        startDate: null,
        endDate: null,
    });
    const [formData, setFormData] = useState({
        lieferant: '',
        rechnungsNummer: null,
        rechnungsDatum: null,
        nettoBetrag: '',
        umsatzsteuer: '',
        steuer: '',
        bruttoBetrag: '',
        fälligkeitsdatum: null,
        zahlungsstatus: '',
        nettoOffen: '',
        invoiceAvailable: ''
    });
    const [errors, setErrors] = useState({});
    const [debouncedValues, setDebouncedValues] = useState({
        nettoBetrag: '',
        nettoOffen: '',
    });
    const [timer, setTimer] = useState(null);
    const { clientId } = useCommonContext();

    const getVerbindlichkeiten = async (filter) => {
        if (clientId) {
            const payload = {
                pageNo,
                perPage,
                lieferant: filter ? filter?.lieferant : filterData?.lieferant,
                rechnungsNummer: filter ? filter?.rechnungsNummer : filterData?.rechnungsNummer || '',
                zahlungsstatus: filter ? filter?.zahlungsstatus : filterData?.zahlungsstatus || '',
                invoiceAvailable: filter ? filter?.invoiceAvailable : filterData?.invoiceAvailable || '',
                clientId,
                startDate:
                    filter?.startDate !== undefined
                        ? (filter.startDate ? dayjs(filter.startDate).format("DD-MM-YYYY") : null)
                        : (filterData?.startDate ? dayjs(filterData?.startDate).format("DD-MM-YYYY") : null),
                endDate:
                    filter?.endDate !== undefined
                        ? (filter.endDate ? dayjs(filter.endDate).format("DD-MM-YYYY") : null)
                        : (filterData?.endDate ? dayjs(filterData?.endDate).format("DD-MM-YYYY") : null),
            }
            const response = await getVerbindlichkeitenLists(payload);
            if (response?.isSuccess) {
                setVerbindlichkeiten([...response.data]);
                setTotalRecords(response.totalRecord);
            }
            else {
                setVerbindlichkeiten([]);
                setTotalRecords(0);
            }
        }
    }

    useEffect(() => {
        getVerbindlichkeiten()
    }, [pageNo, perPage, clientId]);

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'nettoBetrag' || name === 'nettoOffen') {
            // Immediately set the raw value in the form state to avoid losing user input while typing
            setFormData((prev) => ({ ...prev, [name]: value }));

            // Clear the previous timer if it's still active
            if (timer) {
                clearTimeout(timer);
            }

            // Set a new timer for debouncing the formatted value update
            const newTimer = setTimeout(() => {
                // Format the value only after the debounce delay
                const formattedValue = formatGermanAmount(value);
                setDebouncedValues((prev) => ({ ...prev, [name]: formattedValue }));  // Update debounced value after the delay
            }, 500); // 500 ms debounce delay

            setTimer(newTimer);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        setErrors({ ...errors, [name]: "" });
    };

    useEffect(() => {
        if (!debouncedValues?.nettoBetrag || !formData.umsatzsteuer) return;

        const netto = parseFloat(debouncedValues?.nettoBetrag?.split(",")[0]?.replace(".", ""));
        const steuerPercent = parseFloat(formData.umsatzsteuer);

        if (!isNaN(netto) && !isNaN(steuerPercent)) {
            const steuerAmount = (netto * steuerPercent) / 100;
            setFormData(prev => ({
                ...prev,
                steuer: steuerAmount,
                bruttoBetrag: (Number(netto) + Number(steuerAmount))
            }));
        }
    }, [debouncedValues?.nettoBetrag, formData.umsatzsteuer]);

    // Effect to update formData with debounced values
    useEffect(() => {
        const debounceTime = setTimeout(() => {
            // Update the form state after the debounce delay with the formatted values
            setFormData((prev) => ({
                ...prev,
                nettoBetrag: debouncedValues?.nettoBetrag,
                nettoOffen: debouncedValues?.nettoOffen
            }));
        }, 1000); // 2000 ms debounce delay for saving the formatted value

        return () => clearTimeout(debounceTime);
    }, [debouncedValues]);

    // Handle date change
    const handleDateChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        setErrors(prev => ({
            ...prev,
            [name]: "",
        }));
    };

    // Disable past dates in Fälligkeitsdatum
    const disableBeforeInvoiceDate = (date) => {
        if (!formData.rechnungsDatum) return true;
        return dayjs(date).isBefore(dayjs(formData.rechnungsDatum), "day");
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.lieferant) {
            newErrors.lieferant = `${t('verbindlichkeiten_lieferant')} ${t('is_required')}`;
        }

        if (!formData.rechnungsNummer) {
            newErrors.rechnungsNummer = `${t('forderungen_rechnungsNummer')} ${t('is_required')}`;
        }

        if (!formData.rechnungsDatum) {
            newErrors.rechnungsDatum = `${t('forderungen_rechnungsDatum')} ${t('is_required')}`;
        }

        if (!formData.nettoBetrag) {
            newErrors.nettoBetrag = `${t('forderungen_nettoBetrag')} ${t('is_required')}`;
        }
        else {
            const germanNumber = formData?.nettoBetrag?.replace(/\./g, '')?.replace(/,/g, '.');
            const nettoValue = Number(germanNumber);

            if (isNaN(nettoValue) || !Number.isFinite(nettoValue)) {
                newErrors.nettoBetrag = t('verbindlicheitem_netto_betrag_valid_number');
            } else if (nettoValue <= 0) {
                newErrors.nettoBetrag = `${t('forderungen_nettoBetrag')} ${t('cannot_be_negative')}`;
            }
        }

        if (!formData.umsatzsteuer) {
            newErrors.umsatzsteuer = `${t('forderungen_umsatzsteuer')} ${t('is_required')}`;
        }

        if (!formData.fälligkeitsdatum) {
            newErrors.fälligkeitsdatum = `${t('forderungen_fälligkeitsdatum')} ${t('is_required')}`;
        }
        else if (formData?.rechnungsDatum && formData?.fälligkeitsdatum) {
            const invoiceDate = new Date(formData?.rechnungsDatum);
            const dueDate = new Date(formData?.fälligkeitsdatum);

            if (invoiceDate > dueDate) {
                newErrors.fälligkeitsdatum = t('verbindlicheitem_cannot_be_before_rechnungsdatum');
            }
        }

        if (!formData.zahlungsstatus) {
            newErrors.zahlungsstatus = `${t('forderungen_zahlungsstatus')} ${t('is_required')}`;
        }

        if (!formData.invoiceAvailable) {
            newErrors.invoiceAvailable = `${t('common_invoice_available_error')}`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const toCleanNumberString = (value) => {
        if (value == null) return "0";
        const str = String(value);
        const num = Number(str?.replace(/\./g, '')?.replace(',', '.'));
        return String(num);
    };

    const handleEdit = async (editId) => {
        const response = await getByIdVerbindlichkeiten(editId);
        setFormData({
            ...response.data,
            rechnungsDatum: response.data?.rechnungsDatum ? dayjs(response.data?.rechnungsDatum) : null,
            fälligkeitsdatum: response.data?.fälligkeitsdatum ? dayjs(response.data?.fälligkeitsdatum) : null,
            nettoBetrag: formatUpdateNumber(Number(response.data?.nettoBetrag)),
            bruttoBetrag: formatUpdateNumber(Number(response.data?.bruttoBetrag)),
            steuer: formatUpdateNumber(Number(response.data?.steuer)),
            nettoOffen: response.data?.nettoOffen
                ? formatUpdateNumber(Number(response.data.nettoOffen))
                : ''
        });
        setDebouncedValues({
            nettoBetrag: formatUpdateNumber(Number(response.data?.nettoBetrag)),
            nettoOffen: response.data?.nettoOffen ? formatUpdateNumber(Number(response.data?.nettoOffen)) : '',
        });
        setEditId(editId);
        setIsModalOpen(true);
        setErrors({});
    };

    const handleDelete = async (deleteId) => {
        const response = await deleteVerbindlichkeiten(deleteId);
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            getVerbindlichkeiten();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const fälligkeitsdatum = dayjs(formData?.fälligkeitsdatum).format("DD-MM-YYYY");
        const rechnungsDatum = dayjs(formData?.rechnungsDatum).format("DD-MM-YYYY");
        const nettoBetrag = formatPlainAmount(formData?.nettoBetrag);
        const nettoOffen = formatPlainAmount(formData?.nettoOffen);
        const steuer = toCleanNumberString(formData?.steuer);
        const bruttoBetrag = toCleanNumberString(formData?.bruttoBetrag);

        let response = [];
        if (editId) {
            response = await updateVerbindlichkeiten({ ...formData, nettoOffen, clientId, steuer, bruttoBetrag, nettoBetrag, rechnungsDatum, fälligkeitsdatum, id: editId });
        }
        else {
            formData.isManual = true;
            response = await createVerbindlichkeiten({ ...formData, nettoOffen, clientId, steuer, bruttoBetrag, nettoBetrag, rechnungsDatum, fälligkeitsdatum });
        }
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            setIsModalOpen(false);
            getVerbindlichkeiten();
            setFormData({
                lieferant: '',
                rechnungsNummer: null,
                rechnungsDatum: null,
                nettoBetrag: '',
                umsatzsteuer: '',
                steuer: '',
                bruttoBetrag: '',
                fälligkeitsdatum: null,
                zahlungsstatus: '',
                nettoOffen: '',
                invoiceAvailable: ''
            });
            setDebouncedValues({
                nettoBetrag: '',
                nettoOffen: '',
            });
            setTimer(null);
            setEditId(null);
        }
    }

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilterData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAdd = () => {
        setIsModalOpen(true);
    };

    const handleFilterSubmit = () => {
        if (filterData?.kunde?.trim() !== '' || filterData?.rechnungsNummer?.trim() !== "" || filterData?.invoiceAvailable?.trim() !== '' || filterData?.zahlungsstatus?.trim() !== '' || filterData?.startDate !== null || filterData?.endDate !== null) {
            getVerbindlichkeiten();
        }
    };

    const totalNettoOffen = verbindlichkeiten?.reduce((sum, invoice) => sum + Number(invoice?.nettoOffen || 0), 0);

    const excludedKeys = ['_id', 'createdAt', 'updatedAt', 'isDeleted', '__v', 'company', "isManual", 'customers'];

    const customLabels = {
        lieferant: 'Lieferant',
        rechnungsNummer: 'Rechnungsnummer',
        rechnungsDatum: 'Rechnungsdatum',
        fälligkeitsdatum: 'Fälligkeitsdatum',
        nettoBetrag: 'Netto Betrag',
        umsatzsteuer: 'Umsatzsteuer',
        steuer: 'Steuer',
        bruttoBetrag: 'Brutto Betrag',
        nettoOffen: 'Netto Offen',
        zahlungsstatus: 'Zahlungsstatus',
    };

    const customColumnOrder = [
        'lieferant',
        'rechnungsNummer',
        'rechnungsDatum',
        'fälligkeitsdatum',
        'nettoBetrag',
        'umsatzsteuer',
        'steuer',
        'bruttoBetrag',
        'nettoOffen',
        'zahlungsstatus',
    ];

    const formatCell = (key, value) => {
        if (['bruttoBetrag', 'nettoBetrag', 'steuer', 'nettoOffen']?.includes(key)) {
            return formatAmount(value) + " €";
        } else if (['rechnungsDatum', 'fälligkeitsdatum']?.includes(key)) {
            return changeDateFormate(value);
        }
        else if (['umsatzsteuer']?.includes(key)) {
            return `${Number(value)?.toFixed(2)}%`
        }
        return value || '-';
    };

    const handleDateFilterChange = (field, date) => {
        setFilterData(prev => ({
            ...prev,
            [field]: date
        }));
    };

    if (!clientId) {
        return <SelectCustomerPage />
    }

    return (
        <div className="verbindlichkeiten">
            <CommonModal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false); setFormData({
                        lieferant: '',
                        rechnungsNummer: null,
                        rechnungsDatum: null,
                        nettoBetrag: '',
                        umsatzsteuer: '',
                        steuer: '',
                        bruttoBetrag: '',
                        fälligkeitsdatum: null,
                        zahlungsstatus: '',
                        nettoOffen: '',
                        invoiceAvailable: ''
                    });
                    setDebouncedValues({
                        nettoBetrag: '',
                        nettoOffen: '',
                    });
                    setTimer(null);
                    setEditId(null);
                }}
                title={t('verbindlichkeiten_heading')}
                className="model-title"
                width={600}
            >
                <Typography
                    sx={{
                        border: "1px solid #E3E3E3",
                        padding: "20px",
                        borderRadius: "10px",
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>

                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={t('verbindlichkeiten_lieferant')}
                                    label={t('verbindlichkeiten_lieferant')}
                                    className="input-field"
                                    name={'lieferant'}
                                    value={formData?.lieferant || ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.lieferant}
                                    helperText={errors.lieferant}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'0'}
                                    label={t('forderungen_rechnungsNummer')}
                                    className="input-field"
                                    name={'rechnungsNummer'}
                                    value={formData?.rechnungsNummer || ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.rechnungsNummer}
                                    helperText={errors.rechnungsNummer}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale="de"
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_rechnungsDatum')}
                                    </FormLabel>
                                    <DatePicker
                                        value={formData.rechnungsDatum}
                                        onChange={(date) => handleDateChange("rechnungsDatum", date)}
                                        format="DD.MM.YYYY"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#826320',
                                                },
                                            },
                                        }}
                                        slotProps={{
                                            textField: {
                                                error: !!errors.rechnungsDatum,
                                                helperText: errors.rechnungsDatum || '',
                                                fullWidth: true,
                                                InputProps: {
                                                    sx: {
                                                        borderRadius: '10px',
                                                        border: '2px solid #E3E3E3',
                                                        '& .MuiInputBase-input': {
                                                            padding: '10px 12px',
                                                        },
                                                        '& input::placeholder': {
                                                            color: '#000',
                                                            fontSize: '14px',
                                                            opacity: 1,
                                                            fontFamily: 'Poppins'
                                                        }
                                                    }
                                                },
                                                FormHelperTextProps: {
                                                    sx: {
                                                        color: !!errors.rechnungsDatum ? '#d32f2f' : '#14A9E2',
                                                        margin: '5px 0 0 0',
                                                        fontSize: '14px',
                                                        fontFamily: 'Poppins'
                                                    }
                                                }
                                            }
                                        }}
                                    />

                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_nettoBetrag')}
                                    className="input-field"
                                    name={'nettoBetrag'}
                                    value={formData?.nettoBetrag ?? ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors.nettoBetrag}
                                    helperText={errors.nettoBetrag}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    fullWidth
                                    error={!!errors?.umsatzsteuer}
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_umsatzsteuer')}
                                    </FormLabel>
                                    <Select
                                        className="select-input"
                                        fullWidth
                                        value={formData?.umsatzsteuer || ""}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                umsatzsteuer: event.target.value,
                                            });
                                        }}
                                        displayEmpty
                                        inputProps={{
                                            name: 'umsatzsteuer',
                                            IconComponent: () => (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingRight: "10px",
                                                    }}
                                                >
                                                    <ExpandMoreIcon
                                                        sx={{ fontSize: "30px", color: "#33333399" }}
                                                    />
                                                </span>
                                            ),
                                        }}
                                        style={{
                                            padding: "0",
                                            borderRadius: "10px",
                                            border: "1px solid #e3e3e391",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>7%</span>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* Options */}
                                        {[{ value: "0", label: "0%" }, { value: "7", label: "7%" }, { value: "19", label: "19%" }].map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors?.umsatzsteuer && (
                                        <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors?.umsatzsteuer}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_steuer')}
                                    disabled
                                    className="input-field"
                                    name={'steuer'}
                                    value={formData?.steuer ? formatAmount(formData?.steuer) : ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors?.steuer}
                                    helperText={errors?.steuer}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_bruttoBetrag')}
                                    className="input-field"
                                    name={'bruttoBetrag'}
                                    disabled
                                    value={formData?.bruttoBetrag ? formatAmount(formData?.bruttoBetrag) : ""}
                                    onChange={handleChange}
                                    required
                                    error={!!errors?.bruttoBetrag}
                                    helperText={errors?.bruttoBetrag}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale="de"
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_fälligkeitsdatum')}
                                    </FormLabel>
                                    <DatePicker
                                        value={formData.fälligkeitsdatum}
                                        onChange={(date) => handleDateChange("fälligkeitsdatum", date)}
                                        shouldDisableDate={disableBeforeInvoiceDate}
                                        format="DD.MM.YYYY"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#826320',
                                                },
                                            },
                                        }}
                                        slotProps={{
                                            textField: {
                                                error: !!errors?.fälligkeitsdatum,
                                                helperText: errors?.fälligkeitsdatum || '',
                                                name: "fälligkeitsdatum",
                                                fullWidth: true,
                                                InputProps: {
                                                    sx: {
                                                        borderRadius: '10px',
                                                        border: '2px solid #E3E3E3',
                                                        '& .MuiInputBase-input': {
                                                            padding: '10px 12px',
                                                        },
                                                        '& input::placeholder': {
                                                            color: '#000',
                                                            fontSize: '14px',
                                                            opacity: 1,
                                                            fontFamily: 'Poppins'
                                                        }
                                                    }
                                                },
                                                FormHelperTextProps: {
                                                    sx: {
                                                        color: !!errors.fälligkeitsdatum ? '#d32f2f' : '#14A9E2',
                                                        margin: '5px 0 0 0',
                                                        fontSize: '14px',
                                                        fontFamily: 'Poppins'
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InputComponent
                                    type={'text'}
                                    borderColor="E3E3E3"
                                    border="2px solid #E3E3E3"
                                    padding="10px 12px"
                                    placeholder={'100.000,00 EUR'}
                                    label={t('forderungen_nettoOffen')}
                                    className="input-field"
                                    name={'nettoOffen'}
                                    value={formData?.nettoOffen || ""}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    fullWidth
                                    error={!!errors?.zahlungsstatus}
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('forderungen_zurücksetzen')}
                                    </FormLabel>
                                    <Select
                                        className="select-input"
                                        fullWidth
                                        value={formData?.zahlungsstatus ? t(`forderungen_${formData?.zahlungsstatus?.toLowerCase()}`) : ''}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                zahlungsstatus: event.target.value,
                                            });
                                        }}
                                        displayEmpty
                                        inputProps={{
                                            name: 'zahlungsstatus',
                                            IconComponent: () => (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingRight: "10px",
                                                    }}
                                                >
                                                    <ExpandMoreIcon
                                                        sx={{ fontSize: "30px", color: "#33333399" }}
                                                    />
                                                </span>
                                            ),
                                        }}
                                        style={{
                                            padding: "0",
                                            borderRadius: "10px",
                                            border: "1px solid #e3e3e391",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>{t('forderungen_bezahlt')}</span>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* Options */}
                                        {[{ value: "Bezahlt", label: t('forderungen_bezahlt') }, { value: "Unbezahlt", label: t('forderungen_unbezahlt') }].map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors?.zahlungsstatus && (
                                        <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors?.zahlungsstatus}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <FormControl
                                    fullWidth
                                    error={!!errors?.invoiceAvailable}
                                >
                                    <FormLabel
                                        className="label"
                                        sx={{
                                            paddingTop: "1.25rem",
                                            paddingBottom: "10px",
                                            display: "inline-block",
                                            color: "#000",
                                        }}
                                    >
                                        {t('common_invoice_available')}
                                    </FormLabel>
                                    <Select
                                        className="select-input"
                                        fullWidth
                                        value={formData?.invoiceAvailable ? t(`common_${formData?.invoiceAvailable?.toLowerCase()}`) : ''}
                                        onChange={(event) => {
                                            handleChange(event);
                                            setFormData({
                                                ...formData,
                                                invoiceAvailable: event.target.value,
                                            });
                                        }}
                                        displayEmpty
                                        inputProps={{
                                            name: 'invoiceAvailable',
                                            IconComponent: () => (
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        paddingRight: "10px",
                                                    }}
                                                >
                                                    <ExpandMoreIcon
                                                        sx={{ fontSize: "30px", color: "#33333399" }}
                                                    />
                                                </span>
                                            ),
                                        }}
                                        style={{
                                            padding: "0",
                                            borderRadius: "10px",
                                            border: "1px solid #e3e3e391",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>{t('common_ja')}</span>;
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* Options */}
                                        {[{ value: "Ja", label: t('common_ja') }, { value: "Nein", label: t('common_nein') }].map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors?.invoiceAvailable && (
                                        <FormHelperText sx={{ margin: "0", marginTop: "8px", color: "#d32f2f", fontSize: "14px", fontFamily: "Poppins" }}>{errors?.invoiceAvailable}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Typography className="model-action-btn">

                            <CommonButton
                                text={t("forderungen_zurücksetzen")}
                                className="invitation-btn button-border-radius"
                                bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                                onClick={() => {
                                    setFormData({
                                        lieferant: '',
                                        rechnungsNummer: null,
                                        rechnungsDatum: null,
                                        nettoBetrag: '',
                                        umsatzsteuer: '',
                                        steuer: '',
                                        bruttoBetrag: '',
                                        fälligkeitsdatum: null,
                                        zahlungsstatus: '',
                                        nettoOffen: '',
                                        invoiceAvailable: ''
                                    });
                                    setDebouncedValues({
                                        nettoBetrag: '',
                                        nettoOffen: '',
                                    });
                                    setTimer(null);
                                    setErrors({})
                                }}
                            />
                            <CommonButton
                                type="submit"
                                text={`${t("forderungen_speichern")} `}
                                className="add-user-btn button-border-radius"
                            />
                        </Typography>
                    </form>
                </Typography>
            </CommonModal>
            <div>
                <div className="flex justify-between">
                    <Heading text={t('verbindlichkeiten_heading')} />
                </div>
                <div>
                    <Paper elevation={3} sx={{ background: "white", marginBottom: "20px" }} className="forderungenwaltung-border">
                        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ padding: "0" }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: "0", marginBottom: "0" }}>
                                <Typography variant="h6">{t('forderungen_suchfilter')}</Typography>
                            </AccordionSummary>
                            <Divider />
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={4} sx={{ paddingLeft: "0" }}>
                                        <InputComponent
                                            type={'text'}
                                            borderColor="E3E3E3"
                                            border="2px solid #E3E3E3"
                                            padding="10px 12px"
                                            placeholder={t('verbindlichkeiten_lieferant')}
                                            label={t('verbindlichkeiten_lieferant')}
                                            className="input-field"
                                            name={'lieferant'}
                                            value={filterData?.lieferant}
                                            onChange={handleFilterChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <InputComponent
                                            type={'text'}
                                            borderColor="E3E3E3"
                                            border="2px solid #E3E3E3"
                                            padding="10px 12px"
                                            placeholder={'202487'}
                                            label={t('forderungen_rechnungsNummer')}
                                            className="input-field"
                                            name={'rechnungsNummer'}
                                            value={filterData?.rechnungsNummer}
                                            onChange={handleFilterChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <FormControl
                                            fullWidth
                                        >
                                            <FormLabel
                                                className="label"
                                                sx={{
                                                    paddingTop: "1rem",
                                                    paddingBottom: "10px",
                                                    display: "inline-block",
                                                    color: "#000",
                                                }}
                                            >
                                                {t('forderungen_zahlungsstatus')}
                                            </FormLabel>
                                            <Select
                                                className="select-input"
                                                fullWidth
                                                onChange={handleFilterChange}
                                                value={filterData?.zahlungsstatus ? t(`forderungen_${filterData?.zahlungsstatus?.toLowerCase()}`) : ''}
                                                displayEmpty
                                                inputProps={{
                                                    name: 'zahlungsstatus',
                                                    IconComponent: () => (
                                                        <span
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                paddingRight: "10px",
                                                            }}
                                                        >
                                                            <ExpandMoreIcon
                                                                sx={{ fontSize: "30px", color: "#33333399" }}
                                                            />
                                                        </span>
                                                    ),
                                                }}
                                                style={{
                                                    padding: "0",
                                                    borderRadius: "10px",
                                                    border: "1px solid #e3e3e391",
                                                }}
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#999' }}>{t('forderungen_bezahlt')}</span>;
                                                    }
                                                    return selected;
                                                }}
                                            >
                                                {/* Options */}
                                                {[{ value: "Bezahlt", label: t('forderungen_bezahlt') }, { value: "Unbezahlt", label: t('forderungen_unbezahlt') }].map((option, i) => (
                                                    <MenuItem key={i} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <FormControl
                                            fullWidth
                                        >
                                            <FormLabel
                                                className="label"
                                                sx={{
                                                    paddingTop: "1rem",
                                                    paddingBottom: "10px",
                                                    display: "inline-block",
                                                    color: "#000",
                                                }}
                                            >
                                                {t('common_invoice_available')}
                                            </FormLabel>
                                            <Select
                                                className="select-input"
                                                fullWidth
                                                onChange={handleFilterChange}
                                                value={filterData?.invoiceAvailable ? t(`common_${filterData?.invoiceAvailable?.toLowerCase()}`) : ''}
                                                displayEmpty
                                                inputProps={{
                                                    name: 'invoiceAvailable',
                                                    IconComponent: () => (
                                                        <span
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                paddingRight: "10px",
                                                            }}
                                                        >
                                                            <ExpandMoreIcon
                                                                sx={{ fontSize: "30px", color: "#33333399" }}
                                                            />
                                                        </span>
                                                    ),
                                                }}
                                                style={{
                                                    padding: "0",
                                                    borderRadius: "10px",
                                                    border: "1px solid #e3e3e391",
                                                }}
                                                renderValue={(selected) => {
                                                    if (!selected) {
                                                        return <span style={{ color: '#999' }}>{t('common_ja')}</span>;
                                                    }
                                                    return selected;
                                                }}
                                            >
                                                {/* Options */}
                                                {[{ value: "Ja", label: t('common_ja') }, { value: "Nein", label: t('common_nein') }].map((option, i) => (
                                                    <MenuItem key={i} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale="de"
                                        >
                                            <FormLabel
                                                className="label"
                                                sx={{
                                                    paddingTop: "1.25rem",
                                                    paddingBottom: "10px",
                                                    display: "inline-block",
                                                    color: "#000",
                                                }}
                                            >
                                                {t('common_from')}
                                            </FormLabel>
                                            <DatePicker
                                                value={filterData.startDate}
                                                onChange={(date) => {
                                                    handleDateFilterChange("startDate", date);
                                                    // Reset end date when start date changes
                                                    handleDateFilterChange("endDate", null);
                                                }}
                                                format="DD.MM.YYYY"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#826320',
                                                        },
                                                    },
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        InputProps: {
                                                            sx: {
                                                                borderRadius: '10px',
                                                                border: '1px solid #E3E3E3',
                                                                '& .MuiInputBase-input': {
                                                                    padding: '10px 12px',
                                                                },
                                                                '& input::placeholder': {
                                                                    color: '#000',
                                                                    fontSize: '14px',
                                                                    opacity: 1,
                                                                    fontFamily: 'Poppins'
                                                                },
                                                            }
                                                        },
                                                        FormHelperTextProps: {
                                                            sx: {
                                                                color: '#E3E3E3',
                                                                margin: '5px 0 0 0',
                                                                fontSize: '14px',
                                                                fontFamily: 'Poppins'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />

                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid item xs={12} lg={4}>
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale="de"
                                        >
                                            <FormLabel
                                                className="label"
                                                sx={{
                                                    paddingTop: "1.25rem",
                                                    paddingBottom: "10px",
                                                    display: "inline-block",
                                                    color: "#000",
                                                }}
                                            >
                                                {t('common_to')}
                                            </FormLabel>
                                            <DatePicker
                                                value={filterData?.endDate}
                                                onChange={(date) => handleDateFilterChange("endDate", date)}
                                                format="DD.MM.YYYY"
                                                minDate={filterData.startDate} // Don't allow end date before start date
                                                disabled={!filterData.startDate}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#826320',
                                                        },
                                                    },
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        InputProps: {
                                                            sx: {
                                                                borderRadius: '10px',
                                                                border: '1px solid #E3E3E3',
                                                                '& .MuiInputBase-input': {
                                                                    padding: '10px 12px',
                                                                },
                                                                '& input::placeholder': {
                                                                    color: '#000',
                                                                    fontSize: '14px',
                                                                    opacity: 1,
                                                                    fontFamily: 'Poppins'
                                                                },
                                                            }
                                                        },
                                                        FormHelperTextProps: {
                                                            sx: {
                                                                color: '#E3E3E3',
                                                                margin: '5px 0 0 0',
                                                                fontSize: '14px',
                                                                fontFamily: 'Poppins'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />

                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'end', gap: "1rem", marginTop: "1.75rem" }}>
                                    <CommonButton
                                        text={t("forderungen_alleloschen")}
                                        className="invitation-btn button-border-radius"
                                        bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                                        onClick={async () => {
                                            setFilterData({
                                                lieferant: '',
                                                rechnungsNummer: '',
                                                zahlungsstatus: '',
                                                invoiceAvailable: '',
                                                startDate: null,
                                                endDate: null,
                                            });
                                            getVerbindlichkeiten({
                                                lieferant: '',
                                                rechnungsNummer: '',
                                                zahlungsstatus: '',
                                                invoiceAvailable: '',
                                                startDate: null,
                                                endDate: null,
                                            });
                                        }}
                                    />
                                    <CommonButton
                                        type="submit"
                                        onClick={handleFilterSubmit}
                                        text={`${t("forderungen_filteranwenden")}`}
                                        className="add-user-btn button-border-radius"
                                    />
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Paper>
                </div>
                <div className="forderungenwaltung-border">
                    <div className=" flex justify-end add-button-padding">
                        <CommonButton
                            text={t("verbindlichkeiten_button_text")}
                            onClick={handleAdd}
                            icon={
                                <img src={addCircleIcon} alt="addCircleIcon" className="button-icon-padding" />
                            }
                            className="add-user-btn button-border-radius"
                        />
                    </div>
                    <div>
                        <TableComponent
                            data={verbindlichkeiten}
                            setPageNo={setPageNo}
                            pageNo={pageNo}
                            perPage={perPage}
                            setPerPage={setPerPage}
                            totalRecords={totalRecords}
                            columns={columns}
                            className={'table-max-height'}
                        >
                            {verbindlichkeiten?.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.lieferant}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.rechnungsNummer}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(row.rechnungsDatum).format("DD.MM.YYYY")}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(row.fälligkeitsdatum).format("DD.MM.YYYY")}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAmount(row.nettoBetrag) + " €"}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${Number(row.umsatzsteuer)?.toFixed(2)}%`}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAmount(row.steuer) + " €"}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAmount(row.bruttoBetrag) + " €"}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row?.invoiceAvailable ? t(`common_${row?.invoiceAvailable?.toLowerCase()}`) : "-"}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row?.nettoOffen ? formatAmount(row?.nettoOffen) + " €" : '-'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row?.zahlungsstatus ? t(`forderungen_${row?.zahlungsstatus?.toLowerCase()}`) : "-"}</TableCell>
                                    <TableCell>
                                        <Typography
                                            className="action"
                                            classes="flex"
                                            alignItems={"center"}
                                            display={"flex"}
                                        >
                                            <span onClick={() => handleEdit(row._id)}>
                                                <img src={editIcon} alt="edit icon" className="edit-icon" />
                                            </span>
                                            <span onClick={() => handleDelete(row._id)}>
                                                <img src={deleteIcon} alt="delete icon" />
                                            </span>
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableComponent>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', md: 'center' },
                                gap: "1rem",
                                mt: "1.75rem"
                            }}
                        >
                            <Typography>
                                {t('common_sum')} ({t('forderungen_nettoOffen')}):  {formatAmount(totalNettoOffen) + " €"}
                            </Typography>

                            <GeneratePDF
                                orientation={'landscape'}
                                data={verbindlichkeiten}
                                excludedKeys={excludedKeys}
                                customLabels={customLabels}
                                customColumnOrder={customColumnOrder}
                                customRowsPerPage={20}
                                formatCell={formatCell}
                            />
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Verbindlichkeiten